
import Event          from './Event'
import GeoCoordinates from './GeoCoordinates'
import GeoShape       from './GeoShape'
import PostalAddress  from './PostalAddress'
import Thing          from './Thing'
import PropertyValue  from './PropertyValue'

/**
 * Entities that have a somewhat fixed, physical extension.
 * @memberOf things
 */
class Place extends Thing
{
    /**
     * Creates a new Place instance.
     * @constructor
     * @param object The generic object to initialize the object the first time.
     */
    constructor( object = null )
    {
        super() ;

        /**
         * The subject matter of the content.
         * @type {Object|string}
         */
        this.about = null ;

        /**
         * A property-value pair representing an additional characteristics of the entitity, e.g. a product feature or another characteristic for which there is no matching property in schema.org.
         * Can be an Array collection of PropertyValue.
         * @type {Array|PropertyValue}
         */
        this.additionalProperty = null ;

        /**
         * Physical address of the item.
         * @type {PostalAddress|string}
         */
        this.address = null ;

        /**
         * A short textual code (also called "store code") that uniquely identifies a place of business.
         * @type {string}
         */
        this.branchCode = null ;

        /**
         * The basic containment relation between a place and one that contains it.
         * @type {Place}
         */
        this.containedInPlace = null ;

        /**
         * The basic containment relation between a place and another that it contains.
         * @type {Array|Place}
         */
        this.containsPlace = null ;

        /**
         * Upcoming or past event associated with this place, organization, or action.
         * @type {Event|Array}
         */
        this.event = null ;

        /**
         * The fax number.
         * @type {string}
         */
        this.faxNumber = null ;

        /**
         * The geo coordinates of the place.
         * @type {GeoCoordinates|GeoShape}
         */
        this.geo = null ;

        this.set( object ) ;
    }


    /**
     * Clear the object.
     * @return {Object} The current item reference.
     */
    clear()
    {
        super.clear() ;
        this.about              =
        this.additionalProperty =
        this.address            =
        this.branchCode         =
        this.containedInPlace   =
        this.event              =
        this.faxNumber          =
        this.geo                = null ;
        return this ;
    }

    /**
     * Returns a shallow copy of the object.
     * @return {Thing} a shallow copy of the object.
     */
    clone()
    {
        return new Place( this.toObject() ) ;
    }

    /**
     * Populate the object with specific behaviors or types. Launched by default in the constructor and in the set methods.
     * @returns {Thing} The current reference.
     */
    populate()
    {
        const { address , containedInPlace , containsPlace , geo , event } = this ;

        if( address && !(address instanceof PostalAddress))
        {
            this.address = new PostalAddress(address) ;
        }

        if( containedInPlace )
        {
            this.containedInPlace = containedInPlace instanceof Place ? containedInPlace : new Place(containedInPlace)
        }

        if( containsPlace )
        {
            if( containsPlace instanceof Array )
            {
                this.containsPlace = containsPlace.map( item => item instanceof Place ? item : new Place(item) ) ;
            }
            else
            {
                this.containsPlace = new Place( containsPlace ) ;
            }
        }

        if( geo )
        {
            const {
                box , circle, line ,
                latitude , longitude , elevation
            } = geo ;

            if( elevation || latitude || longitude )
            {
                this.geo = new GeoCoordinates( geo ) ;
            }
            else if( box || circle || line )
            {
                this.geo = new GeoShape( geo ) ;
            }
        }

        if( event )
        {
            if( event instanceof Array )
            {
                this.event = event.map( item => new Event(item) ) ;
            }
            else
            {
                this.event = new Event( event ) ;
            }
        }

        return this ;
    }

    /**
     * Returns the generic object representation of the item.
     * @return {Object} The generic object representation of the item.
     */
    toObject()
    {
        let {
            about,
            address,
            additionalProperty,
            branchCode,
            containedInPlace,
            containsPlace,
            event,
            faxNumber,
            geo
        } = this ;

        if( address instanceof PostalAddress )
        {
            address = address.toObject() ;
        }

        if( additionalProperty instanceof Array )
        {
            additionalProperty = additionalProperty.map( prop => (prop instanceof PropertyValue) ? prop.toObject() : prop ) ;
        }
        else if( additionalProperty instanceof PropertyValue )
        {
            additionalProperty = additionalProperty.toObject() ;
        }

        if( containedInPlace instanceof Place )
        {
            containedInPlace = containedInPlace.toObject() ;
        }

        if( containsPlace instanceof Array )
        {
            containsPlace = containsPlace.map( prop => (prop instanceof Place) ? prop.toObject() : prop ) ;
        }
        else if( containsPlace instanceof Place )
        {
            containsPlace = containsPlace.toObject() ;
        }

        if( event instanceof Array )
        {
            event = event.map( item => (item instanceof Event) ? item.toObject() : item ) ;
        }
        else if( event instanceof Event )
        {
            event = event.toObject() ;
        }

        if( geo instanceof GeoCoordinates || geo instanceof GeoShape )
        {
            geo = geo.toObject() ;
        }

        return { ...super.toObject() , ...{
            about              : about ,
            additionalProperty : additionalProperty ,
            address            : address ,
            branchCode         : branchCode ,
            containedInPlace   : containedInPlace ,
            containsPlace      : containsPlace ,
            event              : event ,
            faxNumber          : faxNumber ,
            geo                : geo
        }}
    }
}

/**
 * Indicates if the passed-in object is a Place or a compatible generic object with the good Place definition.
 * @param {Object} object The object to validate.
 * @param {boolean} strict Indicates if all elements must be valid or only one.
 * @returns {boolean} If the object is a valid Place definition.
 */
Place.is = ( object , strict = false ) =>
{
    if( object instanceof Place )
    {
        return true ;
    }
    else if( strict )
    {
        return object.hasOwnProperty('address')
            && object.hasOwnProperty('geo') ;
    }
    else
    {
        return object.hasOwnProperty('address')
            || object.hasOwnProperty('geo') ;
    }
};

export default Place ;


// WEBPACK FOOTER //
// ./src/app/things/Place.js